import { gearboxV3LenderKey } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { GEARBOX_V3_USER_CALL_COUNT } from './userCallBuild'

// Gearbox has no native-ETH path — pools and credit accounts only hold ERC-20
// WETH. Keep underlyings as the wrapped token; `zeroAddress` stays reserved
// for protocols that genuinely expose a native-asset market.

function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}

/**
 * Inject a minimal meta entry for `marketUid` into `metaMap` when the
 * public-data fetcher hasn't supplied one (meta lag, new curator not
 * yet indexed, etc.). Keeps `getMarketUidsFromMeta` aware of the
 * position so the CA's sub-account record still emits — downstream
 * consumers can fill in prices / decimals once real meta catches up.
 */
function ensureMetaStub(
  metaMap: LenderCrossPoolMeta,
  marketUid: string,
  underlying: string,
): void {
  if (metaMap[marketUid]) return
  metaMap[marketUid] = {
    marketUid,
    underlying,
    asset: { address: underlying, decimals: 18, symbol: '' } as any,
    price: { priceUsd: 0, priceUsd24h: 0 } as any,
    depositRate: 0,
    variableBorrowRate: 0,
    stableBorrowRate: 0,
    intrinsicYield: 0,
    rewards: [],
    configs: null,
    flags: {
      isActive: true,
      isFrozen: false,
      borrowingEnabled: false,
      collateralActive: true,
      hasStable: false,
    },
    borrowLiquidity: null,
    withdrawLiquidity: null,
    depositable: null,
    params: null,
  } as any
}

/**
 * Returns a [converter, expectedCallCount] tuple for Gearbox V3.1 user
 * data.
 *
 * Single call `CreditAccountCompressor.getCreditAccounts(cmFilter,
 * caFilter, offset, limit)` returns
 *   `(CreditAccountData[] data, uint256 nextOffset)`.
 *
 * Each `CreditAccountData` describes one isolated Credit Account owned
 * by exactly one CreditManager. We fan out by `creditManager` address
 * and emit one `UserData` per CM under `GEARBOX_V3_<CM_HEX>` — each
 * Credit Account becomes a sub-account under its CM (mirroring the
 * Fluid-NFT / Init-subAccount pattern).
 *
 * V3.0 → V3.1 field renames handled here:
 *   - `isSuccessful`  → `success`
 *   - `ca.addr`       → `ca.creditAccount`
 *   - `ca.balances[]` → `ca.tokens[]`   (with `{token, mask, balance,
 *                                        quota, success}`; "quoted" is
 *                                        now derived from `quota > 0`)
 *
 * `ca.owner` replaces the old implicit "caller passed as borrower"
 * semantic — we don't use it directly since the filter pinned it.
 */
export const getGearboxV3UserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  return [
    (data: any[]) => {
      if (!data || data.length !== GEARBOX_V3_USER_CALL_COUNT) return undefined

      // Response is a tuple `(CreditAccountData[], uint256 nextOffset)`.
      // viem decodes it as an array `[data[], nextOffset]`, NOT a struct
      // with `.data`. If the outer element is a 2-tuple whose first entry
      // is itself an array of objects, that's our CA list. Fall back to
      // treating `first` as the array directly (older decodings, named-
      // struct shape, or a test harness passing CAs already unwrapped).
      const first = data[0]
      let creditAccounts: any[] | undefined
      if (
        Array.isArray(first) &&
        first.length === 2 &&
        Array.isArray(first[0]) &&
        (first[0].length === 0 || typeof first[0][0] === 'object')
      ) {
        creditAccounts = first[0] // tuple [CAs, nextOffset] → extract CAs
      } else if (Array.isArray(first)) {
        creditAccounts = first
      } else {
        creditAccounts = first?.data ?? first?.[0]
      }
      if (!Array.isArray(creditAccounts) || creditAccounts.length === 0)
        return undefined

      // Group Credit Accounts by their CreditManager.
      const perCM = new Map<string, any[]>()
      for (const ca of creditAccounts) {
        if (!ca?.success) continue
        const cm = (ca.creditManager ?? '').toString().toLowerCase()
        if (!cm) continue
        if (!perCM.has(cm)) perCM.set(cm, [])
        perCM.get(cm)!.push(ca)
      }

      if (perCM.size === 0) return undefined

      const out: { [lender: string]: UserData } = {}

      for (const [cmAddr, accounts] of perCM) {
        let lenderKey: string
        try {
          lenderKey = gearboxV3LenderKey(cmAddr)
        } catch {
          continue
        }
        // Soft-gate on missing meta: if public-data hasn't surfaced this
        // CM yet (staging ↔ prod lag, freshly-added curator), use a
        // fresh empty map and populate stub entries per marketUid as we
        // touch them. Without this, empty-state or meta-less CAs
        // silently disappear from `/user-positions`, which breaks
        // integrators whose SubAccountSelector renders buttons off
        // `entry.data` (they'd see `data: []` and fall through to just
        // "+ New", spawning a second CA on the next deposit).
        const metaMap: LenderCrossPoolMeta = meta?.[lenderKey]
          ? { ...meta[lenderKey] } // copy to isolate stub mutations
          : {}

        // All accounts under a CM share the same borrow-asset (pool
        // underlying). Take it from the first account.
        const firstCa = accounts[0]
        const rawUnderlying = (firstCa.underlying ?? '')
          .toString()
          .toLowerCase()
        const underlying = rawUnderlying

        const loanMarketUid = createMarketUid(chainId, lenderKey, underlying)
        ensureMetaStub(metaMap, loanMarketUid, underlying)
        const loanMeta = metaMap[loanMarketUid]
        const loanDec = loanMeta?.asset?.decimals ?? 18
        const loanDisplayPrice = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOraclePrice = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanPriceHist =
          loanMeta?.price?.priceUsd24h ?? loanDisplayPrice

        const lendingPositions: Record<string, Record<string, any>> = {}
        const modes: Record<string, any> = {}
        const histData: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        for (const ca of accounts) {
          const caAddress = (ca.creditAccount ?? ca.addr ?? '')
            .toString()
            .toLowerCase()
          if (!caAddress) continue

          const debtRaw = toBigInt(ca.debt)
          // V3.1: `tokens[]` replaces `balances[]`.
          const tokens: any[] = Array.isArray(ca.tokens)
            ? ca.tokens
            : Array.isArray(ca.balances)
              ? ca.balances
              : []

          // Do NOT skip empty CAs. A freshly-opened account (debt=0,
          // no collateral yet) is still a valid sub-account — same
          // semantic as Init / Fluid emitting an NFT position with
          // zero balances, or Euler emitting a sub-account index the
          // user has touched. Integrators' SubAccountSelector renders
          // one button per entry here, so silently filtering empty
          // CAs strands users on "+ New" and encourages wasteful
          // re-minting.
          //
          // A CA remains in `accounts[]` only while `success==true`
          // (filtered above in the perCM group) and the
          // CreditAccountCompressor only surfaces currently-owned CAs,
          // so we don't need to re-check "closed but not reaped" here.

          modes[caAddress] = 0

          const posData: Record<string, any> = {}

          // ── Collateral entries per token ──────────────────────────
          let totalDepositsUSD = 0
          for (const bal of tokens) {
            const rawToken = (bal?.token ?? '').toString().toLowerCase()
            if (!rawToken) continue
            const token = rawToken
            const balRaw = toBigInt(bal.balance)
            if (balRaw === 0n) continue

            // V3.1: no `isQuoted` flag — a token counts as quoted
            // collateral when its `quota > 0`. V3.0 fallback kept for
            // transition safety.
            const quotaRaw = toBigInt(bal.quota)
            const isQuoted =
              quotaRaw > 0n || !!bal?.isQuoted

            const colMarketUid = createMarketUid(chainId, lenderKey, token)
            ensureMetaStub(metaMap, colMarketUid, token)
            const colMeta = metaMap[colMarketUid]
            const colDec = colMeta?.asset?.decimals ?? 18
            const colDisplayPrice = colMeta ? getDisplayPrice(colMeta) : 0
            const colOraclePrice = colMeta ? getOraclePrice(colMeta) : 0

            const depositsStr = parseRawAmount(balRaw.toString(), colDec)
            const depositsNum = Number(depositsStr)
            const depositsUSD = depositsNum * colDisplayPrice
            const depositsUSDOracle = depositsNum * colOraclePrice
            totalDepositsUSD += depositsUSD

            if (posData[colMarketUid]) {
              // Collateral side coincides with loan side (rare: self-coll
              // markets). Fold the deposit into the existing record.
              const existing = posData[colMarketUid]
              const prevDepositsNum = Number(existing.deposits ?? 0)
              const combinedDeposits = (
                prevDepositsNum + depositsNum
              ).toString()
              posData[colMarketUid] = {
                ...existing,
                deposits: combinedDeposits,
                depositsUSD: existing.depositsUSD + depositsUSD,
                depositsUSDOracle:
                  existing.depositsUSDOracle + depositsUSDOracle,
                collateralEnabled:
                  existing.collateralEnabled || isQuoted,
              }
            } else {
              posData[colMarketUid] = {
                marketUid: colMarketUid,
                underlying: token,
                deposits: depositsStr,
                debt: '0',
                debtStable: '0',
                depositsUSD,
                debtUSD: 0,
                debtStableUSD: 0,
                depositsUSDOracle,
                debtUSDOracle: 0,
                debtStableUSDOracle: 0,
                stableBorrowRate: '0',
                collateralEnabled: isQuoted,
                claimableRewards: 0,
              }
            }
          }

          // ── Debt entry on the loan side ──────────────────────────
          const debtStr = parseRawAmount(debtRaw.toString(), loanDec)
          const debtNum = Number(debtStr)
          const debtUSD = debtNum * loanDisplayPrice
          const debtUSDOracle = debtNum * loanOraclePrice

          if (posData[loanMarketUid]) {
            // The underlying also appears as collateral — merge debt.
            const existing = posData[loanMarketUid]
            posData[loanMarketUid] = {
              ...existing,
              debt: debtStr,
              debtUSD,
              debtUSDOracle,
            }
          } else {
            posData[loanMarketUid] = {
              marketUid: loanMarketUid,
              underlying,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
            }
          }

          histData[caAddress] = {
            totalDeposits24h: totalDepositsUSD,
            totalDebt24h: debtNum * loanPriceHist,
          }

          lendingPositions[caAddress] = posData
        }

        if (Object.keys(lendingPositions).length === 0) continue

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions,
            modes,
          },
          metaMap,
          histData,
        )

        if (userData && userData.data.length > 0) {
          out[lenderKey] = userData
        }
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    GEARBOX_V3_USER_CALL_COUNT,
  ]
}
